import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Car, Airplane, ShieldCheck, Package } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import SEO from "@/components/SEO";

const ServicesPage = () => {
  const { t } = useTranslation();

  const services = [
    {
      id: 1,
      title: t("services_page.service_1_title"),
      subtitle: t("services_page.service_1_subtitle"),
      description: t("services_page.service_1_desc"),
      features: [t("services_page.service_1_f1"), t("services_page.service_1_f2"), t("services_page.service_1_f3"), t("services_page.service_1_f4")],
      path: "/services/deplacements",
      icon: Car,
      image: "https://customer-assets.emergentagent.com/job_parisgo/artifacts/09akaoz4_4DC035C4-C368-4BCB-A49F-408B01512975.jpeg",
      cta: t("services_page.book_now"),
      hasPayment: true,
    },
    {
      id: 2,
      title: t("services_page.service_2_title"),
      subtitle: t("services_page.service_2_subtitle"),
      description: t("services_page.service_2_desc"),
      features: [t("services_page.service_2_f1"), t("services_page.service_2_f2"), t("services_page.service_2_f3"), t("services_page.service_2_f4")],
      path: "/services/greet-meet",
      icon: Airplane,
      image: "/images/greet_meet_altura.png",
      cta: t("services_page.request_quote"),
      hasPayment: false,
    },
    {
      id: 3,
      title: t("services_page.service_3_title"),
      subtitle: t("services_page.service_3_subtitle"),
      description: t("services_page.service_3_desc"),
      features: [t("services_page.service_3_f1"), t("services_page.service_3_f2"), t("services_page.service_3_f3"), t("services_page.service_3_f4")],
      path: "/services/securite",
      icon: ShieldCheck,
      image: "https://images.unsplash.com/photo-1543350155-2cf78fd1c43e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
      cta: t("services_page.request_quote"),
      hasPayment: false,
    },
    {
      id: 4,
      title: t("services_page.service_4_title"),
      subtitle: t("services_page.service_4_subtitle"),
      description: t("services_page.service_4_desc"),
      features: [t("services_page.service_4_f1"), t("services_page.service_4_f2"), t("services_page.service_4_f3"), t("services_page.service_4_f4")],
      path: "/services/transport-luxe",
      icon: Package,
      image: "https://images.pexels.com/photos/35859938/pexels-photo-35859938.jpeg?auto=compress&cs=tinysrgb&w=1600",
      cta: t("services_page.request_quote"),
      hasPayment: false,
    },
  ];

  return (
    <main className="pt-20 bg-[#EDEBE4]" data-testid="services-page">
      <SEO title="Nos Services" path="/services" description="Découvrez les services ALTURA : déplacements sur mesure, accueil Meet & Greet en aéroport, sécurité rapprochée et transport de biens précieux à Paris." />
      {/* Hero */}
      <section className="py-24 sm:py-32 bg-[#EDEBE4] relative">
        <div className="texture-overlay" />
        <div className="max-w-7xl mx-auto px-6 sm:px-12 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <p className="text-xs tracking-[0.2em] uppercase text-[#1A1A1A]/60 mb-6">{t("services_page.hero_subtitle")}</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-normal text-[#1A1A1A] leading-none mb-8">
              {t("services_page.hero_title_1")} <span className="italic">{t("services_page.hero_title_2")}</span>
            </h1>
            <p className="text-[#1A1A1A]/80 text-base lg:text-lg font-light leading-relaxed">
              {t("services_page.hero_desc")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 bg-[#E5E3DC]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="space-y-8">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`grid lg:grid-cols-2 gap-0 ${index % 2 === 1 ? 'lg:grid-flow-dense' : ''}`}
              >
                <div className={`image-zoom h-[300px] lg:h-[500px] ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                  <img src={service.image} alt={service.title} className="w-full h-full object-cover" />
                </div>

                <div className={`bg-white border border-[#1A1A1A]/10 p-8 lg:p-16 flex flex-col justify-center ${index % 2 === 1 ? 'lg:col-start-1' : ''}`}>
                  <service.icon size={40} weight="thin" className="text-[#1A1A1A] mb-6" />
                  <p className="text-xs tracking-[0.2em] uppercase text-[#1A1A1A]/60 mb-2">{service.subtitle}</p>
                  <h2 className="font-heading text-2xl lg:text-3xl text-[#1A1A1A] mb-6">{service.title}</h2>
                  <p className="text-[#1A1A1A]/80 font-light leading-relaxed mb-8">{service.description}</p>
                  
                  <ul className="grid grid-cols-2 gap-4 mb-8">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-3 text-[#1A1A1A]/80 text-sm">
                        <span className="w-1.5 h-1.5 bg-[#1A1A1A] rounded-full" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <Link
                    to={service.path}
                    className={service.hasPayment ? "btn-dark inline-flex items-center gap-3 w-fit" : "btn-primary inline-flex items-center gap-3 w-fit"}
                    data-testid={`service-cta-${service.id}`}
                  >
                    {service.cta}
                    <ArrowRight size={16} weight="bold" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 sm:py-32 bg-[#EDEBE4] relative">
        <div className="texture-overlay" />
        <div className="max-w-4xl mx-auto px-6 sm:px-12 text-center relative">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="font-heading text-3xl sm:text-4xl font-normal text-[#1A1A1A] mb-8">
              {t("services_page.cta_title")}
            </h2>
            <p className="text-[#1A1A1A]/80 font-light mb-12 max-w-2xl mx-auto">{t("services_page.cta_desc")}</p>
            <Link to="/contact" className="btn-dark inline-flex items-center gap-3" data-testid="services-contact-cta">
              {t("services_page.cta_btn")}
              <ArrowRight size={16} weight="bold" />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default ServicesPage;
